import { Navigate, Outlet, useChildMatches, useLocation } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useRef } from "react";
import { isStandalonePage } from "../../shared/public-pages.ts";
import { describeHousehold, forgetSignedOutUser, identify } from "../lib/analytics.ts";
import { AppUpdateProvider } from "../lib/app-update.tsx";
import { useSession } from "../lib/auth-client.ts";
import { usePushAlerts } from "../lib/push-clicks.ts";
import { useBabies } from "../lib/queries.ts";
import { SelectedBabyProvider } from "../lib/selected-baby.tsx";
import { Login } from "../routes/login.tsx";

/** The paper "b" mark, used while anything is loading. */
export function Glyph({ pulse = false }: { pulse?: boolean }) {
  return (
    <span
      className={`heading inline-flex size-12 items-center justify-center text-[34px] ${pulse ? "animate-pulse" : ""}`}
      aria-hidden="true"
    >
      b
    </span>
  );
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center" aria-busy="true">
      {children}
    </div>
  );
}

/** A tapped bottle alert opens its baby's log (lib/push-clicks.ts). */
function PushAlerts() {
  usePushAlerts();
  return null;
}

/**
 * The authenticated app. Fluid: full-bleed on a phone, padded past the notch
 * and the landscape safe areas, and as wide as a tablet allows (each screen
 * decides its own columns; forms keep a readable measure). Redirects a
 * household with no babies to setup, and a set-up household away from it.
 * Watches for a newer deploy (lib/app-update.tsx).
 */
function AppShell() {
  const babies = useBabies();
  const { pathname } = useLocation();
  const babyCount = babies.data?.length;
  useEffect(() => {
    if (babyCount !== undefined) describeHousehold({ babies: babyCount });
  }, [babyCount]);

  if (babies.isPending) {
    return (
      <FullScreen>
        <Glyph pulse />
      </FullScreen>
    );
  }
  if (babies.isError && !babies.data) {
    return (
      <FullScreen>
        <p className="px-6 text-center text-[15px] opacity-70">
          Couldn't load your household. Pull to refresh, or try again in a moment.
        </p>
      </FullScreen>
    );
  }
  const count = babies.data.length;
  if (count === 0 && pathname !== "/setup") return <Navigate to="/setup" replace />;
  if (count > 0 && pathname === "/setup") return <Navigate to="/" replace />;

  return (
    <main
      className="mx-auto min-h-dvh w-full max-w-[1280px] pb-10 pl-[max(20px,env(safe-area-inset-left))] pr-[max(20px,env(safe-area-inset-right))] md:pl-[max(32px,env(safe-area-inset-left))] md:pr-[max(32px,env(safe-area-inset-right))]"
      style={{ paddingTop: "calc(16px + env(safe-area-inset-top))" }}
    >
      <AppUpdateProvider>
        <SelectedBabyProvider>
          <PushAlerts />
          <Outlet />
        </SelectedBabyProvider>
      </AppUpdateProvider>
    </main>
  );
}

function SessionGate({ children }: { children?: React.ReactNode }) {
  // Gate the whole app on a session: loading → glyph, no session → Login,
  // else the authenticated shell. Mounting the data-fetching shell only after
  // a session exists keeps org-scoped queries from firing (and 401-ing) pre-auth.
  const { data: session, isPending, error } = useSession();
  // Analytics: who is using the app, and for which household. A session that
  // is gone (expired or revoked, not a failed check) forgets the parent, so
  // the signed-out pages that follow aren't theirs.
  const user = session?.user;
  const organizationId = session?.session.activeOrganizationId ?? null;
  const signedOut = !isPending && !session && !error;
  useEffect(() => {
    if (user) identify({ id: user.id, email: user.email, name: user.name }, organizationId);
    else if (signedOut) forgetSignedOutUser();
  }, [user, organizationId, signedOut]);
  // A session that ends while the app is open (Sign out, here or in another
  // tab, or an expiry) swaps the screen for the landing page with no
  // navigation, so the window kept the old screen's scroll: signing out from
  // the foot of Settings opened the landing page halfway down. Scroll to the
  // top before that first paint.
  const hadSession = useRef(false);
  useLayoutEffect(() => {
    if (user) hadSession.current = true;
    else if (hadSession.current) {
      hadSession.current = false;
      window.scrollTo(0, 0);
    }
  }, [user]);

  if (isPending) {
    return (
      <FullScreen>
        <Glyph pulse />
      </FullScreen>
    );
  }
  if (!session) return <Login />;
  return children ?? <AppShell />;
}

export function RootLayout() {
  // Decide by the route that <Outlet /> renders, not by useLocation(): during
  // an in-app navigation the location changes first, while the previous route
  // stays on screen until the next one loads. Deciding by the new path let a
  // signed-out visitor's link to /compare mount the log for a moment, and its
  // reads failed with 401 (#84).
  const routeId = useChildMatches({ select: (matches) => matches[0]?.routeId });
  // Legal and support pages must remain readable even if auth or household
  // loading fails, and the comparison page reads the same signed in or out.
  if (isStandalonePage(routeId)) return <Outlet />;
  // An AI assistant asking for access: needs a session, but not a set-up
  // household, so it skips the app shell's setup redirect.
  if (routeId === "/consent")
    return (
      <SessionGate>
        <Outlet />
      </SessionGate>
    );
  return <SessionGate />;
}
